import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardPaste, Copy, Plus, Trash2 } from 'lucide-react';
import { EMBEDDED_LIMITS, type SpecGroup } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Modal } from '@/components/ui/Modal.js';
import { Select, Textarea } from '@/components/ui/Field.js';
import { SortableList } from '@/components/ui/SortableList.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { useToast } from '@/components/ui/Toast.js';
import { queryKeys } from '@/lib/query-keys.js';
import { emptyTranslatable } from '@/features/blocks/BlockFields.js';
import { parseSpecPaste } from './spec-paste.js';
import { productsApi } from './shop-api.js';

export type SpecBuilderProps = {
  value: SpecGroup[];
  onChange: (value: SpecGroup[]) => void;
  /** Nusxa olishda o'zini ro'yxatdan chiqarish uchun. */
  currentProductId?: string;
};

/**
 * Xarakteristikalar konstruktori (TZ 7.3).
 *
 * Bu Google Drive'dagi PDF larni almashtiradigan asosiy vosita, shuning
 * uchun uchta tez yo'l bor: qo'lda kiritish, **Excel'dan yopishtirish**
 * va **boshqa mahsulotdan nusxa olish**. O'xshash modellarda (Mavic 3 va
 * Mavic 3 Pro) 40 qatorni qayta terish real ishda bajarilmasdi.
 */
export function SpecBuilder({ value, onChange, currentProductId }: SpecBuilderProps) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  function updateGroup(index: number, next: SpecGroup): void {
    onChange(value.map((group, i) => (i === index ? next : group)));
  }

  function addGroup(): void {
    onChange([
      ...value,
      { title: emptyTranslatable(), rows: [], order: value.length } as SpecGroup,
    ]);
  }

  const rowTotal = value.reduce((sum, group) => sum + group.rows.length, 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-n-800">
          Guruhlar
          <span className="ml-1.5 text-xs font-normal text-n-400">
            {value.length}/{EMBEDDED_LIMITS.SPEC_GROUPS} · {rowTotal} qator
          </span>
        </span>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            icon={<ClipboardPaste className="size-4" />}
            onClick={() => setPasteOpen(true)}
          >
            Excel'dan yopishtirish
          </Button>
          <Button
            size="sm"
            variant="outline"
            icon={<Copy className="size-4" />}
            onClick={() => setCopyOpen(true)}
          >
            Boshqa mahsulotdan
          </Button>
          <Button
            size="sm"
            icon={<Plus className="size-4" />}
            disabled={value.length >= EMBEDDED_LIMITS.SPEC_GROUPS}
            onClick={addGroup}
          >
            Guruh
          </Button>
        </div>
      </div>

      {value.length === 0 ? (
        <p className=" border border-dashed border-n-200 px-3 py-8 text-center text-sm text-n-600">
          Xarakteristikalar yo‘q. Excel jadvalidan yopishtirish eng tez yo‘l.
        </p>
      ) : (
        <SortableList
          items={value}
          getId={(_, index) => `group-${index}`}
          onReorder={(groups) => onChange(groups.map((group, i) => ({ ...group, order: i })))}
          renderRow={(group, index) => (
            <details className="flex-1 border border-n-200" open={index === 0}>
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="truncate font-medium text-n-800">
                  {group.title.uz || `${index + 1}-guruh`}
                  <span className="ml-1.5 text-xs font-normal text-n-400">
                    {group.rows.length} qator
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Guruhni o‘chirish"
                  onClick={(event) => {
                    event.preventDefault();
                    onChange(value.filter((_, i) => i !== index));
                  }}
                >
                  <Trash2 className="size-4 text-error-600" aria-hidden />
                </Button>
              </summary>

              <div className="space-y-3 border-t border-n-200 px-3 py-3">
                <TranslatableInput
                  label="Guruh nomi"
                  value={group.title}
                  onChange={(title) => updateGroup(index, { ...group, title })}
                />

                <SpecRows
                  rows={group.rows}
                  onChange={(rows) => updateGroup(index, { ...group, rows })}
                />
              </div>
            </details>
          )}
        />
      )}

      <PasteModal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onApply={(groups, mode) => {
          onChange(
            mode === 'replace'
              ? groups
              : [...value, ...groups].map((group, i) => ({ ...group, order: i })),
          );
        }}
      />

      <CopyFromProductModal
        open={copyOpen}
        currentProductId={currentProductId}
        onClose={() => setCopyOpen(false)}
        onApply={(groups) => onChange(groups)}
      />
    </div>
  );
}

function SpecRows({
  rows,
  onChange,
}: {
  rows: SpecGroup['rows'];
  onChange: (rows: SpecGroup['rows']) => void;
}) {
  return (
    <div className="space-y-2">
      <SortableList
        items={rows}
        getId={(_, index) => `row-${index}`}
        onReorder={onChange}
        emptyTitle="Qator yo‘q"
        renderRow={(row, index) => (
          <div className="grid flex-1 gap-2 sm:grid-cols-2">
            <TranslatableInput
              label="Nomi"
              value={row.key}
              onChange={(key) => onChange(rows.map((r, i) => (i === index ? { ...r, key } : r)))}
            />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TranslatableInput
                  label="Qiymati"
                  value={row.value}
                  onChange={(value) =>
                    onChange(rows.map((r, i) => (i === index ? { ...r, value } : r)))
                  }
                />
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Qatorni o‘chirish"
                className="mb-1"
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
              >
                <Trash2 className="size-4 text-error-600" aria-hidden />
              </Button>
            </div>
          </div>
        )}
      />

      <Button
        size="sm"
        variant="outline"
        icon={<Plus className="size-4" />}
        disabled={rows.length >= EMBEDDED_LIMITS.SPEC_ROWS_PER_GROUP}
        onClick={() =>
          onChange([...rows, { key: emptyTranslatable(), value: emptyTranslatable() }])
        }
      >
        Qator qo‘shish
      </Button>
    </div>
  );
}

/** Excel'dan yopishtirish oynasi — natija OLDINDAN ko'rsatiladi. */
function PasteModal({
  open,
  onClose,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  onApply: (groups: SpecGroup[], mode: 'append' | 'replace') => void;
}) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'append' | 'replace'>('append');
  const toast = useToast();

  const parsed = parseSpecPaste(text);

  function apply(): void {
    if (parsed.groups.length === 0) {
      toast.error('Yopishtirilgan matndan qator topilmadi');
      return;
    }
    onApply(parsed.groups, mode);
    toast.success(`${parsed.rowCount} ta qator qo‘shildi`);
    setText('');
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Excel'dan yopishtirish"
      description="Jadvalni Excel yoki Google Sheets'da belgilab, Ctrl+C bosing va shu yerga qo‘ying"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Bekor qilish
          </Button>
          <Button onClick={apply} disabled={parsed.groups.length === 0}>
            Qo‘shish ({parsed.rowCount})
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Jadval"
          rows={10}
          value={text}
          onChange={(event) => setText(event.target.value)}
          className="font-mono text-xs"
          placeholder={'Kamera\nSensor\t1/1.3" CMOS\nDiafragma\tf/1.7'}
          hint="Bitta ustunli qator — guruh sarlavhasi. Ikki ustun — nomi va qiymati"
        />

        <Select
          label="Mavjud guruhlar bilan nima qilish"
          value={mode}
          options={[
            { value: 'append', label: 'Oxiriga qo‘shish' },
            { value: 'replace', label: 'Hammasini almashtirish' },
          ]}
          onChange={(event) => setMode(event.target.value as 'append' | 'replace')}
        />

        {text.trim() !== '' && (
          <div className=" border border-n-200 bg-n-50 p-3">
            <p className="mb-2 text-sm font-medium text-n-800">
              Oldindan ko‘rish: {parsed.groups.length} guruh, {parsed.rowCount} qator
              {parsed.skipped > 0 && ` · ${parsed.skipped} qator o‘qilmadi`}
            </p>
            <div className="max-h-56 space-y-2 overflow-y-auto text-sm">
              {parsed.groups.map((group, index) => (
                <div key={index}>
                  <p className="font-medium text-n-800">{group.title.uz}</p>
                  <ul className="ml-3 text-n-600">
                    {group.rows.map((row, rowIndex) => (
                      <li key={rowIndex}>
                        {row.key.uz} — <span className="text-n-600">{row.value.uz || '—'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Boshqa mahsulotdan nusxa olish — o'xshash modellar uchun (TZ 7.3). */
function CopyFromProductModal({
  open,
  currentProductId,
  onClose,
  onApply,
}: {
  open: boolean;
  currentProductId?: string;
  onClose: () => void;
  onApply: (groups: SpecGroup[]) => void;
}) {
  const [selected, setSelected] = useState('');
  const toast = useToast();

  const params = { limit: 100, sort: 'title' as const };
  const list = useQuery({
    queryKey: queryKeys.content.list('products', { ...params, purpose: 'spec-copy' }),
    queryFn: ({ signal }) => productsApi.list(params, signal),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const options = (list.data?.data ?? [])
    .filter((product) => product._id !== currentProductId)
    .map((product) => ({
      value: product._id,
      label: `${product.title?.uz || product.slug} (${product.specGroups?.length ?? 0} guruh)`,
    }));

  const source = useQuery({
    queryKey: queryKeys.content.one('products', selected),
    queryFn: () => productsApi.one(selected),
    enabled: open && selected !== '',
  });

  function apply(): void {
    const groups = source.data?.specGroups ?? [];
    if (groups.length === 0) {
      toast.error('Tanlangan mahsulotda xarakteristika yo‘q');
      return;
    }
    // Nusxa — mustaqil obyekt. Havola qoldirilsa, bir mahsulotni tahrirlash
    // ikkinchisini ham o'zgartirib yuborardi.
    onApply(structuredClone(groups));
    toast.success(`${groups.length} ta guruh nusxalandi`);
    setSelected('');
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Boshqa mahsulotdan nusxa olish"
      description="Mavjud xarakteristikalar ALMASHTIRILADI"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Bekor qilish
          </Button>
          <Button onClick={apply} disabled={selected === '' || source.isLoading}>
            Nusxalash
          </Button>
        </>
      }
    >
      <Select
        label="Mahsulot"
        value={selected}
        placeholder={list.isLoading ? 'Yuklanmoqda…' : 'Tanlang'}
        options={options}
        onChange={(event) => setSelected(event.target.value)}
      />
    </Modal>
  );
}
